import React, { useEffect, useMemo, useRef, useState } from "react";
import Sidebar from "../Sidebar/Sidebar";
import {
  DataGrid,
  GridActionsCellItem,
  GridToolbarContainer,
} from "@mui/x-data-grid";
import {
  Alert,
  Autocomplete,
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { Redirect } from "react-router-dom";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import LocalstorageHelper from "../../LocalStorageHelper/LocalstorageHelper";

toast.configure();

const OPTIONS_LIMIT = 15;

const filterOptions = createFilterOptions({
  limit: OPTIONS_LIMIT,
  trim: true,
});

const filterOptionsParty = createFilterOptions({
  limit: OPTIONS_LIMIT,
  trim: true,
  matchFrom: "start",
});

function GridTopBar(props) {
  const { rowCount } = props;
  return (
    <GridToolbarContainer>
      <Box
        sx={{
          px: 1.25,
          py: 0.75,
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: "#f8fbff",
          borderBottom: "1px solid #dbe7f3",
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: 13 }}>
          Warranty Issue Rows
        </Typography>
        <Chip
          size="small"
          label={`Rows: ${rowCount}`}
          color="primary"
          variant="outlined"
          sx={{ height: 24 }}
        />
      </Box>
    </GridToolbarContainer>
  );
}

function PartEditAutocomplete(props) {
  const { id, field, value, api, allparts } = props;

  const selected =
    allparts.find((p) => p.itemcode === value || p.itemid === value) || null;

  return (
    <Autocomplete
      size="small"
      options={allparts}
      value={selected}
      autoHighlight
      getOptionLabel={(option) => option?.itemcode || ""}
      isOptionEqualToValue={(option, val) => option?.itemid === val?.itemid}
      onChange={async (event, newValue) => {
        const item = newValue || null;

        await api.setEditCellValue({
          id,
          field,
          value: item?.itemcode || "",
        });

        if (field === "againstitemcode") {
          await api.setEditCellValue({
            id,
            field: "againstitemid",
            value: item?.itemid || null,
          });
          await api.setEditCellValue({
            id,
            field: "againstitemname",
            value: item?.itemname || "",
          });
          await api.setEditCellValue({
            id,
            field: "rackid",
            value: item?.rackid || "",
          });
          await api.setEditCellValue({
            id,
            field: "rackname",
            value: item?.rackname || "",
          });

          const stock = Number(item?.closingqty || 0);
          if (stock <= 0 && item?.itemcode) {
            toast.error(`Low stock / no stock for ${item.itemcode}`);
          }
        }

        if (field === "partyitemcode") {
          await api.setEditCellValue({
            id,
            field: "partyitemid",
            value: item?.itemid || null,
          });
          await api.setEditCellValue({
            id,
            field: "partyitemname",
            value: item?.itemname || "",
          });
        }

        api.commitCellChange({ id, field });
        api.setCellMode(id, field, "view");
      }}
      renderOption={(props2, option) => (
        <li {...props2} key={option.itemid || option.itemcode}>
          <Box sx={{ width: "100%" }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, lineHeight: 1.2 }}>
              {option.itemcode} - {option.itemname}
            </Typography>
            <Typography
              sx={{ fontSize: 11.5, color: "text.secondary", lineHeight: 1.2 }}
            >
              Stock: {Number(option.closingqty || 0)} | Rack:{" "}
              {option.rackname || "N/A"}
            </Typography>
          </Box>
        </li>
      )}
      renderInput={(params) => <TextField {...params} autoFocus />}
    />
  );
}

const WarrantyIssue = () => {
  const userToken = LocalstorageHelper.GetUserToken();

  const [disableSave, setDisableSave] = useState(false);
  const [disableUpdate, setDisableUpdate] = useState(false);

  const [documentdate, setDocumentDate] = useState(dayjs());
  const [docno, setDocNo] = useState("");
  const [mode, setMode] = useState(1);

  const [searchdisabled, setSearchDisabled] = useState(true);
  const [searchbtndisabled, setSearchBtnDisabled] = useState(true);

  const [docopts, setDocOpts] = useState([]);
  const [docdetails, setDocDetails] = useState(null);

  const [partyopts, setPartyOpts] = useState([]);
  const [partydetails, setPartyDetails] = useState(null);

  const [allparts, setAllParts] = useState([]);
  const [rows, setRows] = useState([]);

  const [loadingParts, setLoadingParts] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);

  const [againstPartInput, setAgainstPartInput] = useState("");
  const [againstPart, setAgainstPart] = useState(null);
  const [againstSrlNo, setAgainstSrlNo] = useState("");

  const [partyPartInput, setPartyPartInput] = useState("");
  const [partyPart, setPartyPart] = useState(null);
  const [partySrlNo, setPartySrlNo] = useState("");

  const againstPartRef = useRef(null);
  const againstSrlRef = useRef(null);
  const partyPartRef = useRef(null);
  const partySrlRef = useRef(null);

  const redirectToLoginPage = () => {
    if (!userToken) {
      return <Redirect exact to="/sparepartsLogin" />;
    }
    return null;
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      LocalstorageHelper.RemoveUserToken();
    }, 3600000);
    return () => clearTimeout(timeout);
  }, []);

  const resetEntryFields = () => {
    setAgainstPartInput("");
    setAgainstPart(null);
    setAgainstSrlNo("");
    setPartyPartInput("");
    setPartyPart(null);
    setPartySrlNo("");

    setTimeout(() => {
      if (againstPartRef.current) {
        againstPartRef.current.focus();
      }
    }, 50);
  };

  async function getPartyOpts() {
    try {
      const response = await fetch("/warranty/partydata", {
        method: "GET",
        headers: { sptoken: userToken },
      });
      const jsonData = await response.json();
      setPartyOpts(Array.isArray(jsonData) ? jsonData : []);
    } catch (err) {
      console.error(err.message);
      toast.error("Failed to load party list");
    }
  }

  async function getAllParts() {
    try {
      setLoadingParts(true);
      const response = await fetch("/warranty/issue/bat/allparts", {
        method: "GET",
        headers: { sptoken: userToken },
      });
      const jsonData = await response.json();
      setAllParts(Array.isArray(jsonData) ? jsonData : []);
    } catch (err) {
      console.error(err.message);
      toast.error("Failed to load parts");
    } finally {
      setLoadingParts(false);
    }
  }

  async function getDocNos() {
    try {
      const response = await fetch("/warranty/issue/bat/documentnos", {
        method: "GET",
        headers: { sptoken: userToken },
      });
      const jsonData = await response.json();
      setDocOpts(Array.isArray(jsonData) ? jsonData : []);
    } catch (err) {
      console.error(err.message);
    }
  }

  useEffect(() => {
    getPartyOpts();
    getDocNos();
    getAllParts();
  }, []);

  const handleModeChange = (e) => {
    const selectedMode = parseInt(e.target.value, 10);
    setMode(selectedMode);

    if (selectedMode === 1) {
      setSearchDisabled(true);
      setSearchBtnDisabled(true);
      setDocDetails(null);
      setDocNo("");
      setRows([]);
    //   setPartydetails(null);
      setDocumentDate(dayjs());
    } else {
      setSearchDisabled(false);
      setSearchBtnDisabled(false);
    }
  };

  const fetchPartByCode = async (code) => {
    try {
      const body = { code: String(code || "").trim() };
      if (!body.code) return null;

      const response = await fetch("/warranty/issue/bat/allparts/detailfetch", {
        method: "POST",
        headers: {
          sptoken: userToken,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();
      if (data?.type === "error" || !data?.itemid) return null;
      return data;
    } catch (err) {
      console.error(err.message);
      return null;
    }
  };

  const resolvePartFromInput = async (typedValue, selectedPart) => {
    if (selectedPart?.itemid) return selectedPart;

    const typed = String(typedValue || "").trim();
    if (!typed) return null;

    const localMatch = allparts.find(
      (item) =>
        String(item.itemcode || "").toUpperCase() === typed.toUpperCase()
    );

    if (localMatch) return localMatch;

    const fetched = await fetchPartByCode(typed);
    return fetched || null;
  };

  const handleAgainstPartBlur = async () => {
    if (againstPart?.itemid) return;

    const resolved = await resolvePartFromInput(againstPartInput, againstPart);
    if (resolved) {
      setAgainstPart(resolved);
      setAgainstPartInput(resolved.itemcode || "");

      if (!partyPartInput && !partyPart) {
        setPartyPart(resolved);
        setPartyPartInput(resolved.itemcode || "");
      }
    }
  };

  const handlePartyPartBlur = async () => {
    if (partyPart?.itemid) return;

    const resolved = await resolvePartFromInput(partyPartInput, partyPart);
    if (resolved) {
      setPartyPart(resolved);
      setPartyPartInput(resolved.itemcode || "");
    }
  };

  const handleEntryKeyDown = async (
    event,
    nextRef,
    runBlurLookup = false,
    blurFn = null
  ) => {
    if (event.key === "Enter") {
      event.preventDefault();

      if (runBlurLookup && blurFn) {
        await blurFn();
      }

      if (nextRef?.current) {
        nextRef.current.focus();
      }
    }
  };

  const addRow = async () => {
    const finalAgainstPart = await resolvePartFromInput(
      againstPartInput,
      againstPart
    );

    const finalPartyPart = await resolvePartFromInput(
      partyPartInput,
      partyPart
    );

    if (!partydetails?.subledgerid) {
      toast.error("Select Party first");
      return;
    }

    if (!finalAgainstPart?.itemid) {
      toast.error("Select valid Against Part");
      return;
    }

    const availableStock = Number(finalAgainstPart.closingqty || 0);
    if (availableStock <= 0) {
      toast.error("Low stock / no stock. Cannot add this item.");
      return;
    }

    if (!String(againstSrlNo || "").trim()) {
      toast.error("Against Srl No required");
      return;
    }

    if (!String(partySrlNo || "").trim()) {
      toast.error("Party Srl No required");
      return;
    }

    const derivedPartyCode = finalPartyPart?.itemcode || partyPartInput || "";
    if (!String(derivedPartyCode || "").trim()) {
      toast.error("Select valid Party Part");
      return;
    }

    const newRow = {
      srl: rows.length + 1,
      againstitemcode: finalAgainstPart.itemcode || "",
      againstitemname: finalAgainstPart.itemname || "",
      againstsrlno: againstSrlNo || "",
      partyitemcode: derivedPartyCode,
      partyitemname: finalPartyPart?.itemname || "",
      partysrlno: partySrlNo || "",
      rackname: finalAgainstPart.rackname || "",
      partyid: partydetails.subledgerid,
      againstitemid: finalAgainstPart.itemid || null,
      partyitemid: finalPartyPart?.itemid || null,
      rackid:
        finalAgainstPart.rackid !== undefined && finalAgainstPart.rackid !== null
          ? finalAgainstPart.rackid
          : "",
    };

    setRows((prev) => [...prev, newRow]);
    resetEntryFields();
  };

  const handleDeleteClick = (id) => () => {
    if (window.confirm("Are you sure you want to remove the item?")) {
      setRows((prevRows) =>
        prevRows
          .filter((row) => row.srl !== id)
          .map((row, idx) => ({ ...row, srl: idx + 1 }))
      );
    }
  };

  const processRowUpdate = (newRow) => {
    const againstItem =
      allparts.find((p) => p.itemcode === newRow.againstitemcode) || null;
    const partyItem =
      allparts.find((p) => p.itemcode === newRow.partyitemcode) || null;

    const availableStock = Number(
      againstItem?.closingqty ?? newRow.closingqty ?? 0
    );

    if (!newRow.againstsrlno?.trim()) {
      toast.error("Against Srl No required");
      return newRow;
    }

    if (!newRow.partysrlno?.trim()) {
      toast.error("Party Srl No required");
      return newRow;
    }

    if (againstItem && availableStock <= 0) {
      toast.error(`Low stock / no stock for ${againstItem.itemcode}`);
      return newRow;
    }

    const updatedRow = {
      ...newRow,
      againstitemid: againstItem?.itemid || newRow.againstitemid || null,
      againstitemname: againstItem?.itemname || newRow.againstitemname || "",
      rackid: againstItem?.rackid || newRow.rackid || "",
      rackname: againstItem?.rackname || newRow.rackname || "",
      partyitemid: partyItem?.itemid || newRow.partyitemid || null,
      partyitemname: partyItem?.itemname || newRow.partyitemname || "",
    };

    setRows((prev) =>
      prev.map((row) => (row.srl === updatedRow.srl ? updatedRow : row))
    );

    return updatedRow;
  };

  const columns = useMemo(
    () => [
      {
        field: "action",
        type: "actions",
        headerClassName: "super-app-theme--header",
        headerName: "",
        width: 50,
        getActions: (params) => [
          <GridActionsCellItem
            icon={<DeleteIcon fontSize="small" />}
            label="Delete"
            onClick={handleDeleteClick(params.id)}
            color="inherit"
          />,
        ],
      },
      {
        field: "srl",
        headerName: "Srl",
        width: 55,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "againstitemcode",
        headerName: "Against Part",
        width: 170,
        editable: false,
        headerClassName: "super-app-theme--header",
        renderEditCell: (params) => (
          <PartEditAutocomplete {...params} allparts={allparts} />
        ),
      },
      {
        field: "againstitemname",
        headerName: "Against Description",
        width: 250,
        editable: false,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "againstsrlno",
        headerName: "Against Srl No",
        width: 150,
        editable: true,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "partyitemcode",
        headerName: "Party Part",
        width: 170,
        editable: false,
        headerClassName: "super-app-theme--header",
        renderEditCell: (params) => (
          <PartEditAutocomplete {...params} allparts={allparts} />
        ),
      },
      {
        field: "partyitemname",
        headerName: "Party Description",
        width: 250,
        editable: false,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "partysrlno",
        headerName: "Party Srl No",
        width: 150,
        editable: true,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "rackname",
        headerName: "Rack",
        width: 120,
        editable: false,
        headerClassName: "super-app-theme--header",
      },
    ],
    [rows, allparts]
  );

  const handleSave = async (e) => {
    e.preventDefault();

    if (!partydetails?.subledgerid) {
      toast.error("Select Party");
      return;
    }

    if (!rows.length) {
      toast.error("Add at least one row");
      return;
    }

    for (const r of rows) {
      if (!r.againstitemid) {
        toast.error(`Against item missing at row ${r.srl}`);
        return;
      }
      if (!String(r.againstsrlno || "").trim()) {
        toast.error(`Against srl missing at row ${r.srl}`);
        return;
      }
      if (!String(r.partysrlno || "").trim()) {
        toast.error(`Party srl missing at row ${r.srl}`);
        return;
      }
    }

    setDisableSave(true);
    setPageLoading(true);

    try {
      const body = {
        documentdate,
        partyid: partydetails?.subledgerid,
        rows,
      };

      const response = await fetch("/warranty/issue/bat/insert", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          sptoken: userToken,
        },
        body: JSON.stringify(body),
      });

      const parseRes = await response.json();

      if (parseRes.type === "success") {
        toast.success(parseRes.message, {
          autoClose: false,
          onClose: () => window.location.reload(false),
        });
      } else {
        toast.error(parseRes.message || "Save failed");
        setDisableSave(false);
      }
    } catch (err) {
      console.log(err.message);
      toast.error("Save failed");
      setDisableSave(false);
    } finally {
      setPageLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();

    if (!docdetails?.headerid) {
      toast.error("Select document");
      return;
    }

    setPageLoading(true);

    try {
      const response = await fetch(`/warranty/issue/bat/data/${docdetails.headerid}`, {
        method: "GET",
        headers: { sptoken: userToken },
      });

      const jsonData = await response.json();

      if (jsonData.type === "error") {
        toast.error(jsonData.message || "Search failed");
        return;
      }

      setRows(jsonData.detaildata || []);
      setDocumentDate(dayjs(jsonData.headerdata.documentdate));
      setDocNo(jsonData.headerdata.documentno || "");
      setPartyDetails({
        partydetails: jsonData.headerdata.partydetails,
        subledgerid: jsonData.headerdata.subledgerid,
      });
    } catch (err) {
      console.log(err.message);
      toast.error("Search failed");
    } finally {
      setPageLoading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    if (!docdetails?.headerid) {
      toast.error("Select document");
      return;
    }

    if (!partydetails?.subledgerid) {
      toast.error("Select Party");
      return;
    }

    if (!rows.length) {
      toast.error("No rows to update");
      return;
    }

    setDisableUpdate(true);
    setPageLoading(true);

    try {
      const body = {
        documentdate,
        partyid: partydetails?.subledgerid,
        rows,
      };

      const response = await fetch(`/warranty/issue/bat/update/${docdetails.headerid}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          sptoken: userToken,
        },
        body: JSON.stringify(body),
      });

      const parseRes = await response.json();

      if (parseRes.type === "success") {
        toast.success(parseRes.message, {
          autoClose: false,
          onClose: () => window.location.reload(false),
        });
      } else {
        toast.error(parseRes.message || "Update failed");
        setDisableUpdate(false);
      }
    } catch (err) {
      console.log(err.message);
      toast.error("Update failed");
      setDisableUpdate(false);
    } finally {
      setPageLoading(false);
    }
  };

  return (
    <>
      <Box>
        <Sidebar
          Title={"WARRANTY ISSUE AGAINST CUSTOMER"}
          expandedPanel={"panel2"}
          children={
            <Box component="form" sx={{ width: "100%" }} autoComplete="off">
              <Backdrop
                open={pageLoading || loadingParts}
                sx={{
                  color: "#fff",
                  zIndex: (theme) => theme.zIndex.drawer + 999,
                  backdropFilter: "blur(2px)",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <CircularProgress color="inherit" size={28} />
                <Typography sx={{ fontWeight: 700, fontSize: 13 }}>
                  {loadingParts ? "Loading Parts..." : "Processing..."}
                </Typography>
              </Backdrop>

              <Card elevation={1} sx={{ borderRadius: 2.5, mb: 1.5 }}>
                <CardContent sx={{ p: "10px 14px !important" }}>
                  <Stack
                    spacing={1}
                    direction={{ xs: "column", lg: "row" }}
                    alignItems={{ xs: "stretch", lg: "center" }}
                  >
                    <TextField
                      select
                      label="Mode"
                      value={mode}
                      onChange={handleModeChange}
                      sx={{ width: 110 }}
                      size="small"
                    >
                      <MenuItem value={1}>New</MenuItem>
                      <MenuItem value={2}>Edit</MenuItem>
                    </TextField>

                    <TextField
                      disabled
                      label="Doc No"
                      value={docno}
                      sx={{ width: 130 }}
                      size="small"
                    />

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="Doc Date"
                        inputFormat="DD/MM/YYYY"
                        value={documentdate}
                        onChange={(newValue) => {
                          setDocumentDate(newValue);
                        }}
                        renderInput={(params) => (
                          <TextField {...params} size="small" sx={{ width: 140 }} />
                        )}
                      />
                    </LocalizationProvider>

                    <Autocomplete
                      id="party"
                      value={partydetails}
                      filterOptions={filterOptionsParty}
                      onChange={(event, newValue) => setPartyDetails(newValue)}
                      options={partyopts}
                      getOptionLabel={(option) => option.partydetails || ""}
                      sx={{ minWidth: 280, flex: 1 }}
                      renderInput={(params) => (
                        <TextField
                          required
                          {...params}
                          size="small"
                          label="Party"
                          placeholder="Party"
                        />
                      )}
                    />
                  </Stack>

                  {mode === 2 && (
                    <>
                      <Divider sx={{ my: 1.25 }} />
                      <Stack
                        spacing={1}
                        direction={{ xs: "column", lg: "row" }}
                        alignItems={{ xs: "stretch", lg: "center" }}
                      >
                        <Autocomplete
                          disabled={searchdisabled}
                          id="doc-search"
                          value={docdetails}
                          filterOptions={filterOptions}
                          onChange={(event, newValue) => setDocDetails(newValue)}
                          options={docopts}
                          getOptionLabel={(option) => option.documentno || ""}
                          sx={{ minWidth: 250, flex: 1 }}
                          renderInput={(params) => (
                            <TextField
                              size="small"
                              {...params}
                              placeholder="Search Document"
                              label="Document"
                            />
                          )}
                        />

                        <Button
                          disabled={searchbtndisabled}
                          variant="contained"
                          onClick={handleSearch}
                          startIcon={<SearchRoundedIcon />}
                          sx={{ borderRadius: 2, minWidth: 110, height: 36 }}
                        >
                          Search
                        </Button>
                      </Stack>
                    </>
                  )}
                </CardContent>
              </Card>

              <Card elevation={1} sx={{ borderRadius: 2.5, mb: 1.5 }}>
                <CardContent sx={{ p: "10px 14px !important" }}>
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={1}
                    alignItems={{ xs: "stretch", md: "center" }}
                    flexWrap="wrap"
                    useFlexGap
                  >
                    <Autocomplete
                      freeSolo
                      value={againstPart}
                      inputValue={againstPartInput}
                      filterOptions={filterOptions}
                      onInputChange={(event, newInputValue) => {
                        setAgainstPartInput(newInputValue);
                        if (!newInputValue) setAgainstPart(null);
                      }}
                      onChange={(event, newValue) => {
                        if (typeof newValue === "string") {
                          setAgainstPart(null);
                          setAgainstPartInput(newValue);
                          setPartyPart(null);
                          setPartyPartInput(newValue);
                        } else {
                          setAgainstPart(newValue);
                          setAgainstPartInput(newValue?.itemcode || "");
                          setPartyPart(newValue || null);
                          setPartyPartInput(newValue?.itemcode || "");
                        }
                      }}
                      options={allparts}
                      loading={loadingParts}
                      getOptionLabel={(option) => {
                        if (typeof option === "string") return option;
                        return option?.itemcode || "";
                      }}
                      isOptionEqualToValue={(option, value) =>
                        option?.itemid === value?.itemid
                      }
                      sx={{ width: 420 }}
                      renderOption={(props, option) => (
                        <li {...props} key={option.itemid || option.itemcode}>
                          <Box sx={{ width: "100%" }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 700, lineHeight: 1.2 }}>
                              {option.itemcode} - {option.itemname}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: 11.5,
                                color: "text.secondary",
                                lineHeight: 1.2,
                              }}
                            >
                              Stock: {Number(option.closingqty || 0)} | Rack:{" "}
                              {option.rackname || "N/A"}
                            </Typography>
                          </Box>
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Against Part"
                          placeholder="Type or search part"
                          size="small"
                          inputRef={againstPartRef}
                          onBlur={handleAgainstPartBlur}
                          onKeyDown={(e) =>
                            handleEntryKeyDown(
                              e,
                              againstSrlRef,
                              true,
                              handleAgainstPartBlur
                            )
                          }
                        />
                      )}
                    />

                    <TextField
                      label="Against Srl No"
                      value={againstSrlNo}
                      onChange={(e) => setAgainstSrlNo(e.target.value)}
                      inputRef={againstSrlRef}
                      onKeyDown={(e) => handleEntryKeyDown(e, partyPartRef)}
                      size="small"
                      sx={{ width: 170 }}
                    />

                    <Autocomplete
                      freeSolo
                      value={partyPart}
                      inputValue={partyPartInput}
                      filterOptions={filterOptions}
                      onInputChange={(event, newInputValue) => {
                        setPartyPartInput(newInputValue);
                        if (!newInputValue) setPartyPart(null);
                      }}
                      onChange={(event, newValue) => {
                        if (typeof newValue === "string") {
                          setPartyPart(null);
                          setPartyPartInput(newValue);
                        } else {
                          setPartyPart(newValue);
                          setPartyPartInput(newValue?.itemcode || "");
                        }
                      }}
                      options={allparts}
                      loading={loadingParts}
                      getOptionLabel={(option) => {
                        if (typeof option === "string") return option;
                        return option?.itemcode || "";
                      }}
                      isOptionEqualToValue={(option, value) =>
                        option?.itemid === value?.itemid
                      }
                      sx={{ width: 420 }}
                      renderOption={(props, option) => (
                        <li {...props} key={option.itemid || option.itemcode}>
                          <Box sx={{ width: "100%" }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 700, lineHeight: 1.2 }}>
                              {option.itemcode} - {option.itemname}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: 11.5,
                                color: "text.secondary",
                                lineHeight: 1.2,
                              }}
                            >
                              Stock: {Number(option.closingqty || 0)} | Rack:{" "}
                              {option.rackname || "N/A"}
                            </Typography>
                          </Box>
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Party Part"
                          placeholder="Type or search part"
                          size="small"
                          inputRef={partyPartRef}
                          onBlur={handlePartyPartBlur}
                          onKeyDown={(e) =>
                            handleEntryKeyDown(
                              e,
                              partySrlRef,
                              true,
                              handlePartyPartBlur
                            )
                          }
                        />
                      )}
                    />

                    <TextField
                      label="Party Srl No"
                      value={partySrlNo}
                      onChange={(e) => setPartySrlNo(e.target.value)}
                      inputRef={partySrlRef}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          addRow();
                        }
                      }}
                      size="small"
                      sx={{ width: 170 }}
                    />

                    <Button
                      size="medium"
                      variant="contained"
                      color="primary"
                      startIcon={<AddCircleOutlineRoundedIcon />}
                      onClick={addRow}
                      sx={{
                        borderRadius: 2,
                        px: 1.5,
                        height: 36,
                        minWidth: 105,
                      }}
                    >
                      Add
                    </Button>
                  </Stack>

                  {againstPart && (
                    <Alert
                      severity={
                        Number(againstPart.closingqty || 0) <= 0 ? "error" : "info"
                      }
                      sx={{ mt: 1.25, borderRadius: 2, py: 0 }}
                    >
                      <Typography sx={{ fontSize: 12.5 }}>
                        <strong>{againstPart.itemcode}</strong> -{" "}
                        {againstPart.itemname} | Stock:{" "}
                        {Number(againstPart.closingqty || 0)} | Rack:{" "}
                        {againstPart.rackname || "N/A"}
                      </Typography>
                    </Alert>
                  )}
                </CardContent>
              </Card>

              <Card elevation={1} sx={{ borderRadius: 2.5, mb: 1.5 }}>
                <CardContent sx={{ p: 0 }}>
                  <Box
                    sx={{
                      height: 390,
                      width: "100%",
                      "& .super-app-theme--header": {
                        backgroundColor: "#ebf5ff",
                        color: "#0d3557",
                        fontWeight: 800,
                        fontSize: 12,
                      },
                      "& .MuiDataGrid-cell": {
                        fontSize: 12,
                        py: 0.25,
                      },
                    }}
                  >
                    <DataGrid
                      rows={rows}
                      columns={columns}
                      getRowId={(row) => row.srl}
                      density="compact"
                      rowHeight={30}
                      headerHeight={32}
                      disableSelectionOnClick
                      editMode="cell"
                      processRowUpdate={processRowUpdate}
                      onProcessRowUpdateError={(error) => {
                        console.error(error);
                        toast.error("Failed to update row");
                      }}
                      components={{
                        Toolbar: () => <GridTopBar rowCount={rows.length} />,
                      }}
                      initialState={{
                        sorting: {
                          sortModel: [{ field: "srl", sort: "asc" }],
                        },
                        pagination: {
                          pageSize: 10,
                        },
                      }}
                      rowsPerPageOptions={[10, 20, 50]}
                    />
                  </Box>
                </CardContent>
              </Card>

              <Stack direction="row" spacing={1} justifyContent="center">
                {mode === 1 && (
                  <Button
                    disabled={disableSave}
                    onClick={handleSave}
                    size="medium"
                    variant="contained"
                    color="primary"
                    sx={{ borderRadius: 2, minWidth: 110, height: 36 }}
                  >
                    Save
                  </Button>
                )}

                {mode === 2 && (
                  <Button
                    disabled={disableUpdate}
                    onClick={handleUpdate}
                    size="medium"
                    variant="contained"
                    color="warning"
                    sx={{ borderRadius: 2, minWidth: 110, height: 36 }}
                  >
                    Update
                  </Button>
                )}
              </Stack>
            </Box>
          }
        />
        {redirectToLoginPage()}
      </Box>
    </>
  );
};

export default WarrantyIssue;